import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getMyBusiness,
  updateMyBusiness,
  addImages,
  deleteImage
} from "../../services/businessOwnerService";
import {
  getSubscriptionStatus,
  createOrder,
  verifyPayment,
  loadRazorpayScript,
  openRazorpayCheckout
} from "../../services/subscriptionService";
import { CATEGORIES } from "../../constants/categories";
import { getCategories } from "../../services/categoryService";
import { getCities } from "../../services/cityService";
const MAX_IMAGES = 3;

const STATUS_CONFIG = {
  trial:   { cls: "sub-banner sub-banner--trial",   label: "Free Trial" },
  active:  { cls: "sub-banner sub-banner--active",  label: "Active" },
  grace:   { cls: "sub-banner sub-banner--grace",   label: "Expiring Soon" },
  expired: { cls: "sub-banner sub-banner--expired", label: "Expired" }
};

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function SubscriptionBanner({ sub, onRenew, paying }) {
  const { subscriptionStatus, expiresAt, paidUntil } = sub;
  const cfg = STATUS_CONFIG[subscriptionStatus] || STATUS_CONFIG.expired;
  const showRenew = subscriptionStatus === "expired" || subscriptionStatus === "grace";

  return (
    <div className={cfg.cls}>
      <div className="sub-banner-info">
        <span className="sub-badge">{cfg.label}</span>
        {subscriptionStatus === "active" && paidUntil && (
          <span className="sub-expires">Valid until {fmt(paidUntil)}</span>
        )}
        {subscriptionStatus === "trial" && expiresAt && (
          <span className="sub-expires">Free trial until {fmt(expiresAt)}</span>
        )}
        {subscriptionStatus === "grace" && expiresAt && (
          <span className="sub-expires">Expires {fmt(expiresAt)} — renew to keep your listing live</span>
        )}
        {subscriptionStatus === "expired" && (
          <span className="sub-expires">Your listing is hidden from search. Renew now to go live.</span>
        )}
      </div>
      {showRenew && (
        <button className="sub-renew-btn" onClick={onRenew} disabled={paying}>
          {paying ? "Processing..." : "Renew — ₹100/year"}
        </button>
      )}
    </div>
  );
}

const compressImage = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => { img.src = e.target.result; };
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const max = 1200;
      let { width, height } = img;
      if (width > max || height > max) {
        const scale = Math.min(max / width, max / height);
        width = width * scale;
        height = height * scale;
      }
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], file.name, { type: "image/jpeg" }) : file),
        "image/jpeg",
        0.78
      );
    };
  });

function EditBusiness() {
  const navigate   = useNavigate();
  const fileRef    = useRef(null);

  const [business,      setBusiness]      = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [uploadingImg,  setUploadingImg]  = useState(false);
  const [deletingId,    setDeletingId]    = useState(null);
  const [error,         setError]         = useState("");
  const [subscription,  setSubscription]  = useState(null);
  const [paying,        setPaying]        = useState(false);
  const [categoryList,  setCategoryList]  = useState(CATEGORIES);
  const [cityList,      setCityList]      = useState([]);
  const [errors,        setErrors]        = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [isDirty,         setIsDirty]         = useState(false);

  const [form, setForm] = useState({
    businessName: "",
    ownerName:    "",
    category:     "",
    services:     "",
    city:         "",
    address:      "",
    description:  "",
    email:        "",
    website:      ""
  });

  useEffect(() => {
    if (!localStorage.getItem("businessOwnerToken")) {
      navigate("/business-owner/login", { replace: true });
      return;
    }
    fetchBusiness();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getCategories()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const names = data.filter((c) => c.active).map((c) => c.name);
          if (names.length > 0) setCategoryList(names);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getCities()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setCityList(data);
      })
      .catch(() => {});
  }, []);

  const fetchBusiness = async () => {
    setLoading(true);
    setError("");
    try {
      const [data, sub] = await Promise.all([getMyBusiness(), getSubscriptionStatus()]);
      setBusiness(data);
      setSubscription(sub);
      const loaded = {
        businessName: data.businessName || "",
        ownerName:    data.ownerName    || "",
        category:     data.category     || "",
        services:     data.services     || "",
        city:         data.city         || "",
        address:      data.address      || "",
        description:  data.description  || "",
        email:        data.email        || "",
        website:      data.website      || ""
      };
      setForm(loaded);
    } catch (err) {
      if (err?.response?.status === 401) handleLogout();
      else setError(err?.response?.data?.msg || "Failed to load business details.");
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async () => {
    setPaying(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error("Payment system unavailable. Please try again."); return; }

      const order = await createOrder();
      openRazorpayCheckout(
        order,
        async (paymentResponse) => {
          try {
            const result = await verifyPayment(paymentResponse);
            toast.success("Subscription activated! Your listing is now active.");
            setSubscription((prev) => ({
              ...prev,
              subscriptionStatus: result.subscriptionStatus,
              paidUntil: result.paidUntil
            }));
          } catch {
            toast.error("Payment received but activation failed. Contact support.");
          }
        },
        () => toast.info("Payment cancelled.")
      );
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to initiate payment.");
    } finally {
      setPaying(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.businessName.trim()) errs.businessName = "Business name is required";
    else if (form.businessName.trim().length < 3) errs.businessName = "Minimum 3 characters";
    if (!form.ownerName.trim()) errs.ownerName = "Owner name is required";
    if (!form.category) errs.category = "Please select a category";
    if (!form.services.trim()) errs.services = "Please describe your services";
    if (!form.city) errs.city = "Please select your district";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Invalid email address";
    return errs;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await updateMyBusiness(form);
      toast.success("Business details updated successfully!");
      setIsDirty(false);
    } catch (err) {
      if (err?.response?.status === 401) handleLogout();
      else {
        const msg = err?.response?.data?.msg || "Failed to save changes.";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleImagePick = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const currentCount = business?.images?.length || 0;
    if (currentCount >= MAX_IMAGES) {
      toast.error("Maximum 3 images already uploaded. Delete one first.");
      return;
    }

    const allowed = files.slice(0, MAX_IMAGES - currentCount);
    setUploadingImg(true);

    try {
      const compressed = await Promise.all(allowed.map(compressImage));
      const fd = new FormData();
      compressed.forEach((f) => fd.append("images", f));

      const data = await addImages(fd);
      setBusiness((prev) => ({ ...prev, images: data.images }));
      toast.success("Image(s) uploaded!");
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Image upload failed.");
    } finally {
      setUploadingImg(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDeleteImage = async (publicId) => {
    if (confirmDeleteId !== publicId) {
      setConfirmDeleteId(publicId);
      return;
    }
    setConfirmDeleteId(null);
    setDeletingId(publicId);
    try {
      const data = await deleteImage(publicId);
      setBusiness((prev) => ({ ...prev, images: data.images }));
      toast.success("Image deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to delete image.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleBack = () => {
    if (isDirty) {
      toast.info("You have unsaved changes. Please save or discard before leaving.", { toastId: "unsaved-warn" });
      return;
    }
    navigate(-1);
  };

  const handleLogout = () => {
    localStorage.removeItem("businessOwnerToken");
    localStorage.removeItem("businessOwnerName");
    navigate("/business-owner/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="owner-page">
        <div className="owner-card" style={{ textAlign: "center" }}>
          Loading your business details...
        </div>
      </div>
    );
  }

  const images       = business?.images || [];
  const canAddMore   = images.length < MAX_IMAGES;

  return (
    <div className="owner-page">
      <div className="owner-card edit-business-card">

        {/* Header */}
        <div className="edit-page-header">
          <h2>Edit Business</h2>
          <button type="button" className="edit-back-btn" onClick={handleBack}>
            ← Back
          </button>
        </div>

        {error && <div className="owner-error">{error}</div>}

        {/* ── SUBSCRIPTION STATUS BANNER ── */}
        {subscription && <SubscriptionBanner sub={subscription} onRenew={handleRenew} paying={paying} />}

        {/* ── IMAGE MANAGEMENT ── */}
        <div className="image-manager">
          <p className="image-manager-label">
            Photos ({images.length} / {MAX_IMAGES})
          </p>

          <div className="image-manager-grid">
            {images.map((img) => (
              <div key={img.public_id} className="image-thumb-wrap">
                <img
                  src={img.url}
                  alt="Business"
                  className="image-thumb"
                  loading="lazy"
                />
                {confirmDeleteId === img.public_id ? (
                  <div className="image-delete-confirm">
                    <button
                      type="button"
                      className="image-confirm-yes"
                      onClick={() => handleDeleteImage(img.public_id)}
                      disabled={deletingId === img.public_id}
                    >
                      {deletingId === img.public_id ? "…" : "Delete"}
                    </button>
                    <button
                      type="button"
                      className="image-confirm-no"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="image-delete-btn"
                    onClick={() => setConfirmDeleteId(img.public_id)}
                    disabled={deletingId === img.public_id}
                    aria-label="Delete image"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {canAddMore && (
              <label
                className={`image-add-slot ${uploadingImg ? "uploading" : ""}`}
                title="Add photo"
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagePick}
                  disabled={uploadingImg}
                  style={{ display: "none" }}
                />
                {uploadingImg ? (
                  <span className="image-add-icon">⏳</span>
                ) : (
                  <>
                    <span className="image-add-icon">+</span>
                    <span className="image-add-text">Add Photo</span>
                  </>
                )}
              </label>
            )}
          </div>
        </div>

        <hr className="owner-divider" />

        {/* ── DETAILS FORM ── */}
        <form onSubmit={handleSave}>

          <p className="edit-section-label">Business Info</p>

          <div className="owner-form-group">
            <label htmlFor="businessName">Business Name</label>
            <input id="businessName" name="businessName" type="text"
              value={form.businessName} onChange={handleChange} maxLength={100}
              className={errors.businessName ? "input-error" : ""} />
            {errors.businessName && <span className="field-error-msg">{errors.businessName}</span>}
          </div>

          <div className="owner-form-group">
            <label htmlFor="ownerName">Owner Name</label>
            <input id="ownerName" name="ownerName" type="text"
              value={form.ownerName} onChange={handleChange} maxLength={60}
              className={errors.ownerName ? "input-error" : ""} />
            {errors.ownerName && <span className="field-error-msg">{errors.ownerName}</span>}
          </div>

          <div className="owner-form-group">
            <label htmlFor="category">Category</label>
            <select id="category" name="category" value={form.category} onChange={handleChange}
              className={errors.category ? "input-error" : ""}>
              <option value="" disabled>Select a category</option>
              {!categoryList.includes(form.category) && form.category && (
                <option value={form.category} disabled>{form.category} — not in current list</option>
              )}
              {categoryList.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {errors.category && <span className="field-error-msg">{errors.category}</span>}
          </div>

          <div className="owner-form-group">
            <label htmlFor="services">Services Offered</label>
            <textarea id="services" name="services"
              value={form.services} onChange={handleChange} maxLength={300} rows={3}
              className={errors.services ? "input-error" : ""} />
            <div className="char-counter">{form.services.length} / 300</div>
            {errors.services && <span className="field-error-msg">{errors.services}</span>}
          </div>

          <div className="owner-form-group">
            <label htmlFor="description">Description</label>
            <textarea id="description" name="description"
              value={form.description} onChange={handleChange} maxLength={1000} rows={4} />
            <div className="char-counter">{form.description.length} / 1000</div>
          </div>

          <hr className="owner-divider" />
          <p className="edit-section-label">Location</p>

          <div className="owner-form-group">
            <label htmlFor="city">District / City</label>
            <select id="city" name="city" value={form.city} onChange={handleChange}
              className={errors.city ? "input-error" : ""}>
              <option value="" disabled>Select your district</option>
              {!cityList.some((c) => c.name === form.city) && form.city && (
                <option value={form.city} disabled>{form.city} — please select your district</option>
              )}
              {Array.from(new Set(cityList.map((c) => c.state))).map((state) => (
                <optgroup key={state} label={state}>
                  {cityList.filter((c) => c.state === state).map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {errors.city && <span className="field-error-msg">{errors.city}</span>}
          </div>

          <div className="owner-form-group">
            <label htmlFor="address">Address</label>
            <textarea id="address" name="address"
              value={form.address} onChange={handleChange} maxLength={300} rows={2} />
            <div className="char-counter">{form.address.length} / 300</div>
          </div>

          <hr className="owner-divider" />
          <p className="edit-section-label">Contact</p>

          <div className="owner-form-group">
            <label htmlFor="phone">Mobile Number <span className="field-readonly-note">(cannot be changed)</span></label>
            <input id="phone" type="tel" value={business?.phone || ""} readOnly className="input-readonly" />
          </div>

          <div className="owner-form-group">
            <label htmlFor="email">Email <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
            <input id="email" name="email" type="email"
              value={form.email} onChange={handleChange}
              className={errors.email ? "input-error" : ""} />
            {errors.email && <span className="field-error-msg">{errors.email}</span>}
          </div>

          <div className="owner-form-group">
            <label htmlFor="website">Website <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
            <input id="website" name="website" type="text" placeholder="example.com"
              value={form.website} onChange={handleChange} />
          </div>

          <div className="edit-actions-row">
            <button type="submit" className="owner-btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>

          <div className="edit-logout-row">
            <button type="button" className="edit-logout-link" onClick={handleLogout}>
              Logout
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default EditBusiness;
